import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
  ChevronRight,
  KeyRound,
  CreditCard,
  ShieldCheck,
  Tablet,
  LogIn,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useSanitarioAuth } from "../context/SanitarioAuthContext";
import AreaLoginShell from "../components/AreaLoginShell";

const MARK_URL = "/images/logo-sanita-mark.png";
const LOCKUP_URL = "/images/logo-sanita.png";

function getInitials(s) {
  return `${s.nome?.[0] ?? ""}${s.cognome?.[0] ?? ""}`.toUpperCase();
}

export default function SanitarioLogin() {
  const {
    sessions,
    activeOperatore,
    switchTo,
    loginWithPassword,
    loginWithPin,
    loginWithBadge,
  } = useSanitarioAuth();
  const [mode, setMode] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [badge, setBadge] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [switchingId, setSwitchingId] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAddMode = searchParams.get("add") === "1";

  if (activeOperatore && !isAddMode) {
    navigate("/sanitario", { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "password") {
        await loginWithPassword(email, password);
      } else if (mode === "pin") {
        await loginWithPin({ pin, email: email || undefined });
      } else {
        await loginWithBadge({ badge });
      }
      toast.success("Accesso effettuato");
      navigate("/sanitario", { replace: true });
    } catch (error) {
      const detail = error.response?.data?.detail || "Accesso non riuscito";
      toast.error(typeof detail === "string" ? detail : "Accesso non riuscito");
    } finally {
      setLoading(false);
    }
  };

  const handleSwitch = async (id) => {
    setSwitchingId(id);
    try {
      await switchTo(id);
    } finally {
      setSwitchingId(null);
    }
  };

  const showSessionList = sessions.length > 0 && !isAddMode;

  return (
    <AreaLoginShell
      accent="rose"
      currentArea="sanitario"
      title="Modulo sanitario per il lavoro sul posto di cura"
      subtitle="Scheda ospite, parametri, terapie e diario sul tablet — senza dati amministrativi del gestionale."
      logo={
        <img
          src={LOCKUP_URL}
          alt="Sanità by Affidya"
          className="mx-auto mb-3 h-11 w-auto lg:mx-0 lg:mb-9 lg:h-12"
        />
      }
      features={[
        {
          icon: Tablet,
          title: "Pensato per il tablet",
          text: "Accesso con email, PIN o badge, rapido al letto dell'ospite.",
        },
        {
          icon: ShieldCheck,
          title: "Solo dati sanitari",
          text: "Nessuna fatturazione, utenti o abbonamenti in questa area.",
        },
      ]}
    >
      <div className="mb-6 text-center">
        <img src={MARK_URL} alt="Sanità by Affidya" className="mx-auto mb-3 h-12 w-12 rounded-2xl object-contain" />
        <h2 className="text-xl font-semibold tracking-tight text-slate-900">
          {showSessionList ? "Accedi con un altro utente" : "Accedi al modulo"}
        </h2>
        <p className="mt-1 text-sm text-slate-500">Solo funzioni sanitarie operative</p>
      </div>

      {showSessionList ? (
        <div className="mb-5 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Continua come</p>
          {sessions.map((s) => (
            <button
              key={s.id}
              type="button"
              className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left transition hover:border-slate-300 hover:bg-slate-50"
              onClick={() => handleSwitch(s.id)}
              disabled={switchingId === s.id}
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#C83048] text-sm font-semibold text-white">
                {switchingId === s.id ? (
                  <span className="h-5 w-5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                ) : (
                  getInitials(s)
                )}
              </div>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-slate-800">
                  {s.nome} {s.cognome}
                </span>
                <span className="block text-xs text-slate-500">Operatore sanitario</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
            </button>
          ))}
        </div>
      ) : null}

      <div className="mb-4 flex gap-1 rounded-xl bg-slate-100 p-1">
        {[
          { id: "password", label: "Email", icon: LogIn },
          { id: "pin", label: "PIN", icon: KeyRound },
          { id: "badge", label: "Badge", icon: CreditCard },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setMode(id)}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-semibold transition ${
              mode === id ? "bg-white text-[#C83048] shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {mode === "password" ? (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="san-email" className="text-sm font-medium text-slate-700">
                Email o codice
              </Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  id="san-email"
                  type="text"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@email.it oppure ABCD-EFGH"
                  className="h-11 rounded-xl border-slate-200 bg-slate-50/50 pl-10 text-sm"
                  required
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="san-password" className="text-sm font-medium text-slate-700">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  id="san-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 rounded-xl border-slate-200 bg-slate-50/50 pl-10 pr-10 text-sm"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </>
        ) : null}

        {mode === "pin" ? (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="san-pin" className="text-sm font-medium text-slate-700">
                PIN (4–8 cifre)
              </Label>
              <Input
                id="san-pin"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                placeholder="••••"
                className="h-14 rounded-xl text-center text-2xl font-semibold tracking-[0.4em]"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="san-pin-email" className="text-sm font-medium text-slate-700">
                Email operatore <span className="text-slate-400">(consigliata)</span>
              </Label>
              <Input
                id="san-pin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Per evitare ambiguità sul tablet"
                className="h-11 rounded-xl border-slate-200 bg-slate-50/50 text-sm"
              />
            </div>
          </>
        ) : null}

        {mode === "badge" ? (
          <div className="space-y-1.5">
            <Label htmlFor="san-badge" className="text-sm font-medium text-slate-700">
              Codice badge
            </Label>
            <Input
              id="san-badge"
              value={badge}
              onChange={(e) => setBadge(e.target.value)}
              placeholder="Scansiona o digita il codice"
              className="h-14 rounded-xl text-center text-lg font-semibold tracking-wide"
              autoFocus
              required
            />
            <p className="text-xs text-slate-500">
              Configura il badge dalle Impostazioni del modulo dopo il primo accesso con email e password.
            </p>
          </div>
        ) : null}

        <Button
          type="submit"
          disabled={loading}
          className="h-11 w-full rounded-xl bg-[#C83048] text-sm font-semibold text-white hover:bg-[#A8283C]"
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Accedi al modulo
        </Button>
      </form>
    </AreaLoginShell>
  );
}
